/**
 * Manage a machine from the console (workbench D257, AIOE 0.37.0): what the machine says it holds,
 * one "Ask the machine to…" menu, and the orders it was given with the machine's answer by name.
 * The same dialog serves a project's "ask every node" and shows the batch as it is answered.
 */

import { type DeviceOrderBatch, fitHuman, type ManifestSetting, parseRunnerSetting, RUNNER_SETTINGS } from '@aioe/shared';
import { Dropdown, Label, Modal } from '@heroui/react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';

import { routes } from '../api/client.ts';
import { isCloud } from '../edition.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { BACKEND_TEXT, batchSummary, bytesInWords, type DeviceFacts, type DeviceOrder, dlpInWords, factsOf, FIT_WORDS, fitInWords, fitOn, gpusFor, hardwareInWords, KIND_TEXT, MENU_KINDS, orderLine, type OrderKind, type RunnerBackend, runnerInWords, type RunnerFacts, STATE_MARK, STATE_TEXT } from './nodeOrders.ts';
import { Button, ChoiceSelect, ErrorNote, inputClass, ListBlock, ListGroup, StatusMark, Tick } from './ui.tsx';

const KINDS = MENU_KINDS;
const OTHER = '__other';
const KEEP = '__keep';

const DLP_GUIDE = 'A DLP order changes every repository on the machine, not one project.';
const SETTING_GUIDE = "A setting order writes the machine's own value; it is not a lock. A lock comes from the organisation's policy.";
const RUNNER_GUIDE = 'The machine checks every change against its own GPUs before it writes anything, and says in words what it cannot do.';

/** A project's node, by name and what it reports: for the fit line and the runner's choices. */
type Target = { name: string; hardware?: DeviceFacts['hardware']; runner?: RunnerFacts };

/** A model the organisation approved: a catalogue entry of kind `model`. */
interface ApprovedModel {
  id: string;
  name: string;
  repo: string;
  file: string;
  sha256?: string;
  sizeBytes?: number;
  contextLength?: number;
  use?: string;
}

function useApprovedModels(): ApprovedModel[] {
  const cat = useLoad(() => routes.modelCatalogue().catch(() => undefined), []);
  return useMemo(
    () =>
      (cat.data?.entries ?? [])
        .filter((e) => (e.kind as string) === 'model')
        .flatMap((e): ApprovedModel[] => {
          const c = (e.content && typeof e.content === 'object' ? e.content : {}) as Record<string, unknown>;
          const repo = typeof c.repo === 'string' ? c.repo : '';
          const file = typeof c.file === 'string' ? c.file : '';
          if (!repo || !file) return [];
          return [{ id: e.id, name: typeof c.name === 'string' && c.name ? c.name : e.name, repo, file, sha256: typeof c.sha256 === 'string' ? c.sha256 : undefined, sizeBytes: typeof c.sizeBytes === 'number' ? c.sizeBytes : undefined, contextLength: typeof c.contextLength === 'number' ? c.contextLength : undefined, use: typeof c.use === 'string' ? c.use : undefined }];
        }),
    [cat.data],
  );
}

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <label className="grid gap-1 text-xs text-muted">
    {label}
    {children}
  </label>
);

/** The menu that starts an order: the one control a machine's Orders part carries. */
export function AskMenu({ onChoose, label = 'Ask the machine to…' }: { onChoose: (kind: OrderKind) => void; label?: string }) {
  return (
    <Dropdown>
      <Button size="sm" variant="secondary">
        {label}
      </Button>
      <Dropdown.Popover placement="bottom end" className="min-w-52">
        <Dropdown.Menu aria-label={label} onAction={(key) => onChoose(String(key) as OrderKind)}>
          {KINDS.map((k) => (
            <Dropdown.Item key={k} id={k} textValue={KIND_TEXT[k]}>
              <Label>{KIND_TEXT[k]}</Label>
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown>
  );
}

const DECISIONS = { allow: 'Allow', redact: 'Redact', block: 'Block', ask: 'Ask the person' } as const;
const SENSITIVITIES = ['public', 'internal', 'confidential', 'restricted'] as const;

/** The DLP clause's fields; every one starts as "Leave as it is", so an order carries only what the person changed. */
function DlpFields({ onBody }: { onBody: (b: Record<string, unknown> | undefined) => void }) {
  const [classify, setClassify] = useState(KEEP);
  const [secrets, setSecrets] = useState(KEEP);
  const [pii, setPii] = useState(KEEP);
  const [injection, setInjection] = useState(KEEP);
  const [unclassified, setUnclassified] = useState(KEEP);
  const [block, setBlock] = useState<string[] | undefined>();
  const [model, setModel] = useState('');
  const [budget, setBudget] = useState('');
  useEffect(() => {
    const b: Record<string, unknown> = {};
    if (classify !== KEEP) b.classify = classify === 'on';
    if (secrets !== KEEP) b.onSecrets = secrets;
    if (pii !== KEEP) b.onPii = pii;
    if (injection !== KEEP) b.onPromptInjection = injection;
    if (unclassified !== KEEP) b.onUnclassified = unclassified;
    if (block) b.blockSensitivity = SENSITIVITIES.filter((s) => block.includes(s));
    if (model.trim()) b.classifier = { model: model.trim() };
    const ms = Number(budget);
    if (budget && Number.isFinite(ms)) b.latencyBudgetMs = Math.min(5000, Math.max(50, Math.round(ms)));
    onBody(Object.keys(b).length ? b : undefined);
  }, [classify, secrets, pii, injection, unclassified, block, model, budget, onBody]);
  const keep = { [KEEP]: 'Leave as it is' };
  return (
    <div className="grid gap-3">
      <p className="m-0 text-xs text-muted">{DLP_GUIDE}</p>
      <Field label="Check content with a classifier model">
        <ChoiceSelect label="Classifier" className="w-full" value={classify} options={{ ...keep, on: 'On', off: 'Off' }} onChange={setClassify} />
      </Field>
      <Field label="Classifier model">
        <input className={inputClass} value={model} onChange={(e) => setModel(e.target.value)} placeholder="Leave empty to keep the model it uses" />
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Secrets">
          <ChoiceSelect label="Secrets" className="w-full" value={secrets} options={{ ...keep, ...DECISIONS }} onChange={setSecrets} />
        </Field>
        <Field label="Personal information">
          <ChoiceSelect label="Personal information" className="w-full" value={pii} options={{ ...keep, ...DECISIONS }} onChange={setPii} />
        </Field>
        <Field label="Prompt injection">
          <ChoiceSelect label="Prompt injection" className="w-full" value={injection} options={{ ...keep, allow: 'Allow', block: 'Block', ask: 'Ask the person' }} onChange={setInjection} />
        </Field>
        <Field label="When a check cannot finish">
          <ChoiceSelect label="When a check cannot finish" className="w-full" value={unclassified} options={{ ...keep, allow: 'Carry on', ask: 'Ask the person', block: 'Block' }} onChange={setUnclassified} />
        </Field>
      </div>
      <Field label="Time allowed for a check, in milliseconds (50 to 5000)">
        <input type="number" min={50} max={5000} className={`${inputClass} w-32`} value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="300" />
      </Field>
      <div className="grid gap-1.5 text-sm">
        <Tick checked={block !== undefined} onChange={(on) => setBlock(on ? ['restricted'] : undefined)}>
          Change what is blocked by sensitivity
        </Tick>
        {block && (
          <div className="flex flex-wrap gap-x-4 gap-y-1 pl-6">
            {SENSITIVITIES.map((s) => (
              <Tick key={s} checked={block.includes(s)} onChange={(on) => setBlock(SENSITIVITIES.filter((x) => (x === s ? on : block.includes(x))))}>
                {s[0]!.toUpperCase() + s.slice(1)}
              </Tick>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** A setting to write on the machine: only the keys the organisation may lock, from the registered manifests. */
function SettingFields({ onBody }: { onBody: (b: Record<string, unknown> | undefined) => void }) {
  const manifest = useLoad(() => routes.currentPolicyManifest().then((r) => r.manifest).catch(() => undefined), []);
  const settings = useMemo(() => (manifest.data?.settings ?? []).filter((s) => s.lockable), [manifest.data]);
  const [key, setKey] = useState<string>();
  const [text, setText] = useState('');
  const [clear, setClear] = useState(false);
  const def: ManifestSetting | undefined = settings.find((s) => s.key === key);
  useEffect(() => {
    if (!def) return onBody(undefined);
    if (clear) return onBody({ key: def.key, value: null });
    if (text === '') return onBody(undefined);
    if (def.type === 'boolean') return onBody(text === 'true' || text === 'false' ? { key: def.key, value: text === 'true' } : undefined);
    if (def.type === 'number') return onBody(Number.isFinite(Number(text)) ? { key: def.key, value: Number(text) } : undefined);
    if (def.type === 'array' || def.type === 'object') {
      try {
        return onBody({ key: def.key, value: JSON.parse(text) as unknown });
      } catch {
        return onBody(undefined);
      }
    }
    onBody({ key: def.key, value: text });
  }, [def, text, clear, onBody]);
  const options = Object.fromEntries(settings.map((s) => [s.key, s.key])) as Record<string, string>;
  return (
    <div className="grid gap-3">
      <p className="m-0 text-xs text-muted">{SETTING_GUIDE}</p>
      {manifest.data && settings.length === 0 ? <p className="m-0 text-sm text-muted">No registered build offers a setting the organisation may set.</p> : null}
      <Field label="Setting">
        <ChoiceSelect
          label="Setting"
          className="w-full"
          value={key}
          placeholder="Choose a setting"
          options={options}
          onChange={(k) => {
            setKey(k);
            setText('');
            setClear(false);
          }}
        />
      </Field>
      {def?.description ? <p className="m-0 text-xs text-muted">{def.description}</p> : null}
      {def && (
        <Field label="Value">
          {def.type === 'boolean' ? (
            <ChoiceSelect label="Value" className="w-full" isDisabled={clear} value={text || undefined} placeholder="Choose" options={{ true: 'On', false: 'Off' }} onChange={setText} />
          ) : def.enum?.length ? (
            <ChoiceSelect label="Value" className="w-full" isDisabled={clear} value={text || undefined} placeholder="Choose" options={Object.fromEntries(def.enum.map((v) => [v, v]))} onChange={setText} />
          ) : def.type === 'array' || def.type === 'object' ? (
            <textarea className={`${inputClass} mono min-h-20`} disabled={clear} value={text} onChange={(e) => setText(e.target.value)} spellCheck={false} placeholder={def.type === 'array' ? '["one", "two"]' : '{ }'} />
          ) : (
            <input type={def.type === 'number' ? 'number' : 'text'} className={inputClass} disabled={clear} value={text} onChange={(e) => setText(e.target.value)} />
          )}
        </Field>
      )}
      {def && (
        <Tick checked={clear} onChange={setClear}>
          Clear it: the machine goes back to its own default
        </Tick>
      )}
    </div>
  );
}

/** The settings shown first; the rest wait under "More settings". */
const FIRST_SETTINGS = ['contextSize', 'gpuLayers'] as const;
const MORE_SETTINGS = Object.keys(RUNNER_SETTINGS).filter((k) => !(FIRST_SETTINGS as readonly string[]).includes(k));

const sentenceCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** One runner setting from its spec: a number field with its range, or a choice that starts at "Leave as it is". */
function RunnerSetting({ name, value, now, onChange }: { name: string; value: string; now?: unknown; onChange: (v: string) => void }) {
  const spec = RUNNER_SETTINGS[name]!;
  const label = sentenceCase(spec.words);
  const current = now === undefined ? undefined : typeof now === 'boolean' ? (now ? 'on' : 'off') : String(now);
  if (spec.kind === 'int')
    return (
      <Field label={label}>
        <input type="number" inputMode="numeric" min={spec.min} max={spec.max} className={`${inputClass} tabular-nums`} value={value} onChange={(e) => onChange(e.target.value)} placeholder={current !== undefined ? `Now ${current}` : `${spec.min}–${spec.max}`} />
      </Field>
    );
  const options: Record<string, string> = { [KEEP]: current !== undefined ? `Leave as it is (${current})` : 'Leave as it is' };
  if (spec.kind === 'bool') Object.assign(options, { true: 'On', false: 'Off' });
  else for (const v of spec.values ?? []) options[v] = v === 'auto' ? 'Automatic' : v === 'on' ? 'On' : v === 'off' ? 'Off' : v;
  return (
    <Field label={label}>
      <ChoiceSelect label={label} className="w-full" value={value || KEEP} options={options} onChange={(v) => onChange(v === KEEP ? '' : v)} />
    </Field>
  );
}

/**
 * `runner.configure` (D277): the backend from what the machine can run, the GPU when that backend drives more
 * than one, an approved model with its fit, the load settings from their specs, and start or stop. Every field
 * starts as "Leave as it is", so the order carries only what the person changed. For a project the choices
 * are what any of its nodes can run, and each node picks its own GPU.
 */
function RunnerFields({ runner, hardware, targets, approved, onBody, onTooBig }: { runner?: RunnerFacts; hardware?: DeviceFacts['hardware']; targets?: Target[]; approved: ApprovedModel[]; onBody: (b: Record<string, unknown> | undefined) => void; onTooBig: (big: boolean) => void }) {
  const [backend, setBackend] = useState<string>(KEEP);
  const [gpu, setGpu] = useState<string>(KEEP);
  const [model, setModel] = useState<string>(KEEP);
  const [start, setStart] = useState<string>(KEEP);
  const [values, setValues] = useState<Record<string, string>>({});
  const reported = targets ? targets.flatMap((t) => (t.runner ? [t.runner] : [])) : runner ? [runner] : [];
  const backends = [...new Set(reported.flatMap((r) => r.backends))];
  const chosenBackend = backend === KEEP ? runner?.backend : (backend as RunnerBackend | 'auto');
  const gpus = runner && !targets ? gpusFor(chosenBackend, runner) : [];
  const showGpu = gpus.length > 1;
  const chosen = approved.find((a) => a.id === model);

  const problems: string[] = [];
  const settings: Record<string, unknown> = {};
  for (const [k, raw] of Object.entries(values)) {
    if (raw === '') continue;
    const parsed = parseRunnerSetting(k, raw);
    if (parsed.ok) settings[k] = parsed.value;
    else problems.push(parsed.message);
  }
  const context = typeof settings.contextSize === 'number' && settings.contextSize > 0 ? settings.contextSize : chosen?.contextLength;
  const sized = chosen ? { sizeBytes: chosen.sizeBytes, contextLength: context } : undefined;
  const fitLine = sized ? fitInWords(sized, targets ?? [{ name: 'This machine', hardware }]) : undefined;
  const tooBig = !targets && !!sized && fitOn(sized, hardware)?.verdict === 'too-big';

  const body: Record<string, unknown> = {};
  if (backend !== KEEP) body.backend = backend;
  if (showGpu && gpu !== KEEP) body.gpu = gpu === 'auto' || gpu === 'all' ? gpu : Number(gpu);
  if (chosen) body.model = { id: chosen.id, name: chosen.name };
  if (Object.keys(settings).length) body.settings = settings;
  if (start !== KEEP) body.start = start === 'start';
  // The body travels as its JSON, so the dialog hears of a change only when what would be sent changes.
  const sent = JSON.stringify(!problems.length && Object.keys(body).length ? body : null);
  useEffect(() => {
    onBody((JSON.parse(sent) as Record<string, unknown> | null) ?? undefined);
    onTooBig(tooBig);
  }, [sent, tooBig, onBody, onTooBig]);

  if (!reported.length) return <p className="m-0 text-sm text-muted">{targets ? 'No node of this project has reported its local model runner yet.' : 'This node has not reported its local model runner yet.'}</p>;

  const set = (k: string) => (v: string) => setValues((x) => ({ ...x, [k]: v }));
  const backendOptions: Record<string, string> = { [KEEP]: runner && !targets ? `Leave as it is (${BACKEND_TEXT[runner.backend]})` : 'Leave as it is', auto: BACKEND_TEXT.auto };
  for (const b of backends) backendOptions[b] = BACKEND_TEXT[b];
  const gpuOptions: Record<string, string> = { [KEEP]: 'Leave as it is', auto: 'Automatic', all: 'All of them' };
  for (const g of gpus) gpuOptions[String(g.index)] = `${g.name}${g.memoryMB ? ` (${fitHuman(g.memoryMB * 1024 * 1024)})` : ''}`;
  const modelOptions: Record<string, string> = { [KEEP]: runner?.model && !targets ? `Leave as it is (${runner.model.name})` : 'Leave as it is' };
  for (const a of approved) {
    const fit = !targets ? fitOn({ sizeBytes: a.sizeBytes, contextLength: a.contextLength }, hardware) : undefined;
    modelOptions[a.id] = `${a.name}${fit ? ` · ${FIT_WORDS[fit.verdict]}` : bytesInWords(a.sizeBytes) ? ` (${bytesInWords(a.sizeBytes)})` : ''}`;
  }
  const serving = !targets && runner?.serving;
  const now = (k: string) => (!targets ? (runner?.settings as Record<string, unknown> | undefined)?.[k] : undefined);

  return (
    <div className="grid gap-3">
      <p className="m-0 text-xs text-muted">{RUNNER_GUIDE}</p>
      {runner && !targets ? (
        <p className="m-0 flex flex-wrap items-baseline gap-x-2 text-sm">
          <StatusMark state={runner.serving ? 'online' : 'offline'} label={runner.serving ? 'Serving' : 'Stopped'} />
          <span>{runnerInWords(runner)}</span>
        </p>
      ) : null}
      <div className={`grid gap-3 ${showGpu ? 'sm:grid-cols-2' : ''}`}>
        <Field label="Backend">
          <ChoiceSelect
            label="Backend"
            className="w-full"
            value={backend}
            options={backendOptions}
            onChange={(v) => {
              setBackend(v);
              setGpu(KEEP);
            }}
          />
        </Field>
        {showGpu ? (
          <Field label="GPU">
            <ChoiceSelect label="GPU" className="w-full" value={gpu} options={gpuOptions} onChange={setGpu} />
          </Field>
        ) : null}
      </div>
      <Field label="Model">
        <ChoiceSelect label="Model" className="w-full" value={model} options={modelOptions} onChange={setModel} />
      </Field>
      {fitLine ? (
        <p className="m-0 text-xs" role="status">
          <StatusMark state={tooBig ? 'error' : 'online'} label={fitLine} />
        </p>
      ) : null}
      {!approved.length ? <p className="m-0 text-xs text-muted">Publish a model in the Catalogue to offer it here.</p> : null}
      <div className="grid gap-3 sm:grid-cols-3">
        {FIRST_SETTINGS.map((k) => (
          <RunnerSetting key={k} name={k} value={values[k] ?? ''} now={now(k)} onChange={set(k)} />
        ))}
        <Field label="Start or stop">
          <ChoiceSelect label="Start or stop" className="w-full" value={start} options={{ [KEEP]: 'Leave as it is', start: serving ? 'Restart it' : 'Start it', stop: 'Stop it' }} onChange={setStart} />
        </Field>
      </div>
      <details className="group">
        <summary className="w-fit cursor-pointer list-none text-sm text-muted hover:text-foreground [&::-webkit-details-marker]:hidden">More settings</summary>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          {MORE_SETTINGS.map((k) => (
            <RunnerSetting key={k} name={k} value={values[k] ?? ''} now={now(k)} onChange={set(k)} />
          ))}
        </div>
      </details>
      {problems.length ? <p className="m-0 text-xs text-danger" role="alert">{problems[0]}</p> : null}
      {runner?.unchecked?.length && !targets ? <p className="m-0 text-xs text-muted">{runner.unchecked.join(' ')}</p> : null}
    </div>
  );
}

/** The dialog for one kind of order. `facts` is the machine's own report; without it (a project's whole set of nodes) removals pick from the approved list and the registered runtimes. */
export function AskDialog({ kind, facts, deviceName, targets, onClose, onSend }: { kind: OrderKind; facts?: DeviceFacts; deviceName?: string; /** A project's nodes, by name and what each reports, for the fit line and the runner's choices. */ targets?: Target[]; onClose: () => void; onSend: (kind: OrderKind, body: Record<string, unknown>, force?: boolean) => Promise<void> }) {
  const approved = useApprovedModels();
  const vocabulary = useLoad(() => routes.teamVocabulary().catch(() => undefined), []);
  const [body, setBody] = useState<Record<string, unknown>>();
  const [pick, setPick] = useState<string>();
  const [other, setOther] = useState({ repo: '', file: '', sha256: '', name: '' });
  const [runtimeText, setRuntimeText] = useState('');
  const [error, setError] = useState<string>();
  const [sending, setSending] = useState(false);
  const [runnerTooBig, setRunnerTooBig] = useState(false);
  const free = !isCloud();

  const held = facts?.models;
  const runtimes = new Map<string, string>();
  for (const r of vocabulary.data?.runtimes ?? []) runtimes.set(r.id, r.name);
  const heldRuntimes = facts?.runtimes.filter((r) => r.installed);

  let fields: ReactNode;
  let ready: Record<string, unknown> | undefined;
  let fitLine: string | undefined;
  let tooBig = false;
  if (kind === 'model.download') {
    const m = approved.find((a) => a.id === pick);
    const fitTargets = targets ?? (deviceName ? [{ name: deviceName, hardware: facts?.hardware }] : []);
    if (m) {
      fitLine = fitInWords(m, fitTargets);
      // One machine and it cannot hold the model: the person may still send it, knowingly.
      tooBig = !targets && fitTargets.length === 1 && fitOn(m, fitTargets[0]!.hardware)?.verdict === 'too-big';
    }
    const options: Record<string, string> = Object.fromEntries(approved.map((a) => [a.id, `${a.name}${bytesInWords(a.sizeBytes) ? ` (${bytesInWords(a.sizeBytes)})` : ''}`]));
    if (free) options[OTHER] = 'Another model…';
    const isOther = pick === OTHER;
    ready = m
      ? { repo: m.repo, file: m.file, ...(m.sha256 ? { sha256: m.sha256 } : {}), name: m.name, catalogued: true }
      : isOther && other.repo.trim() && other.file.trim()
        ? { repo: other.repo.trim(), file: other.file.trim(), ...(other.sha256.trim() ? { sha256: other.sha256.trim() } : {}), name: other.name.trim() || other.file.trim() }
        : undefined;
    fields = (
      <div className="grid gap-3">
        <Field label="Model">
          <ChoiceSelect label="Model" className="w-full" value={pick} placeholder={approved.length || free ? 'Choose a model' : 'No model is approved yet'} options={options} onChange={setPick} />
        </Field>
        {m?.use ? <p className="m-0 text-xs text-muted">{m.use}</p> : null}
        {fitLine ? (
          <p className="m-0 text-xs" role="status">
            <StatusMark state={tooBig ? 'error' : 'online'} label={fitLine} />
          </p>
        ) : null}
        {!approved.length && !free ? <p className="m-0 text-xs text-muted">Publish a model in the Catalogue first; a machine here only downloads approved models.</p> : null}
        {isOther && (
          <div className="grid gap-3">
            <p className="m-0 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs">This model is not on the approved list.</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Repository">
                <input className={`${inputClass} mono`} value={other.repo} onChange={(e) => setOther({ ...other, repo: e.target.value })} placeholder="owner/model-GGUF" />
              </Field>
              <Field label="File">
                <input className={`${inputClass} mono`} value={other.file} onChange={(e) => setOther({ ...other, file: e.target.value })} placeholder="model-Q4_K_M.gguf" />
              </Field>
            </div>
            <Field label="Name">
              <input className={inputClass} value={other.name} onChange={(e) => setOther({ ...other, name: e.target.value })} />
            </Field>
            <Field label="Digest (SHA-256), if you have one">
              <input className={`${inputClass} mono`} value={other.sha256} onChange={(e) => setOther({ ...other, sha256: e.target.value })} />
            </Field>
          </div>
        )}
      </div>
    );
  } else if (kind === 'model.remove') {
    const items: Array<{ k: string; label: string; body: Record<string, unknown> }> = held
      ? held.map((x) => ({ k: x.id, label: `${x.name}${bytesInWords(x.sizeBytes) ? ` (${bytesInWords(x.sizeBytes)})` : ''}`, body: { id: x.id, name: x.name } }))
      : approved.map((a) => ({ k: a.id, label: a.name, body: { repo: a.repo, file: a.file, ...(a.sha256 ? { sha256: a.sha256 } : {}), name: a.name, catalogued: true } }));
    ready = items.find((i) => i.k === pick)?.body;
    fields = (
      <Field label={held ? 'A model this machine holds' : 'Model'}>
        <ChoiceSelect label="Model" className="w-full" value={pick} placeholder={items.length ? 'Choose a model' : 'The machine reports no model'} options={Object.fromEntries(items.map((i) => [i.k, i.label]))} onChange={setPick} />
      </Field>
    );
  } else if (kind === 'runtime.install') {
    const options: Record<string, string> = Object.fromEntries([...runtimes]);
    options[OTHER] = 'Another runtime…';
    ready = pick === OTHER ? (runtimeText.trim() ? { id: runtimeText.trim() } : undefined) : pick ? { id: pick, name: runtimes.get(pick) } : undefined;
    fields = (
      <div className="grid gap-3">
        <Field label="Runtime">
          <ChoiceSelect label="Runtime" className="w-full" value={pick} placeholder="Choose a runtime" options={options} onChange={setPick} />
        </Field>
        {pick === OTHER && (
          <Field label="Runtime name or id">
            <input className={`${inputClass} mono`} value={runtimeText} onChange={(e) => setRuntimeText(e.target.value)} />
          </Field>
        )}
      </div>
    );
  } else if (kind === 'runtime.remove') {
    const items: Array<{ id: string; name?: string; version?: string }> = heldRuntimes ?? [...runtimes].map(([id, name]) => ({ id, name }));
    const chosen = items.find((r) => r.id === pick);
    ready = chosen ? { id: chosen.id, ...(chosen.name ? { name: chosen.name } : {}) } : undefined;
    fields = (
      <Field label={heldRuntimes ? 'A runtime this machine holds' : 'Runtime'}>
        <ChoiceSelect label="Runtime" className="w-full" value={pick} placeholder={items.length ? 'Choose a runtime' : 'The machine reports no runtime'} options={Object.fromEntries(items.map((r) => [r.id, `${r.name ?? r.id}${r.version ? ` ${r.version}` : ''}`]))} onChange={setPick} />
      </Field>
    );
  } else if (kind === 'dlp.configure') {
    fields = <DlpFields onBody={setBody} />;
    ready = body;
  } else if (kind === 'runner.configure') {
    fields = (
      <RunnerFields
        runner={facts?.runner}
        hardware={facts?.hardware}
        targets={targets}
        approved={approved}
        onBody={setBody}
        onTooBig={setRunnerTooBig}
      />
    );
    ready = body;
    tooBig = runnerTooBig;
  } else {
    fields = <SettingFields onBody={setBody} />;
    ready = body;
  }

  async function send() {
    if (!ready) return;
    setError(undefined);
    setSending(true);
    try {
      await onSend(kind, ready, tooBig || undefined);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSending(false);
    }
  }

  return (
    <Modal isOpen onOpenChange={(open) => !open && onClose()}>
      <Modal.Backdrop>
        <Modal.Container placement="center" size={kind === 'runner.configure' ? 'lg' : 'md'}>
          <Modal.Dialog aria-label={KIND_TEXT[kind]}>
            <Modal.Header>
              <Modal.Heading>{KIND_TEXT[kind]}</Modal.Heading>
            </Modal.Header>
            <Modal.Body className="grid gap-3 p-1">
              {deviceName ? <p className="m-0 text-xs text-muted">{deviceName} applies it once, records it in the audit and answers the next time it reports in.</p> : null}
              {fields}
              <ErrorNote error={error} />
            </Modal.Body>
            <Modal.Footer>
              <Button size="sm" variant="tertiary" onPress={onClose}>
                Cancel
              </Button>
              <Button size="sm" isPending={sending} isDisabled={!ready} onPress={() => void send()}>
                {tooBig ? 'Send anyway' : 'Send'}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}

function FactRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-0.5 sm:grid-cols-[8rem_1fr] sm:gap-3">
      <dt className="text-xs text-muted sm:pt-0.5">{label}</dt>
      <dd className="m-0 text-sm">{children}</dd>
    </div>
  );
}

/** What the machine reports it holds, in plain rows. */
export function MachineFacts({ facts }: { facts?: DeviceFacts }) {
  if (!facts) return <p className="m-0 text-sm text-muted">This machine has not reported what it holds yet.</p>;
  const models = facts.models;
  const runtimes = facts.runtimes.filter((r) => r.installed);
  return (
    <dl className="m-0 grid gap-2">
      <FactRow label="Models">{models.length ? models.map((m) => `${m.name}${m.serving ? ' (serving)' : ''}${bytesInWords(m.sizeBytes) ? ` (${bytesInWords(m.sizeBytes)})` : ''}`).join(', ') : <span className="text-muted">None downloaded</span>}</FactRow>
      <FactRow label="Runtimes">{runtimes.length ? runtimes.map((r) => `${r.name}${r.version ? ` ${r.version}` : ''}${r.disabled ? ' (turned off)' : ''}`).join(', ') : <span className="text-muted">None installed</span>}</FactRow>
      {facts.hardware ? <FactRow label="Hardware">{hardwareInWords(facts.hardware)}</FactRow> : null}
      {facts.runner ? (
        <FactRow label="Local model">
          <span className="flex flex-wrap items-baseline gap-x-2">
            <StatusMark state={facts.runner.serving ? 'online' : 'offline'} label={facts.runner.serving ? 'Serving' : 'Stopped'} />
            <span>{runnerInWords(facts.runner)}</span>
          </span>
          {facts.runner.unchecked?.length ? <span className="block text-xs text-muted">{facts.runner.unchecked.join(' ')}</span> : null}
        </FactRow>
      ) : null}
      {facts.dlp ? <FactRow label="Data protection">{dlpInWords(facts.dlp)}</FactRow> : null}
      {facts.measuredAt ? <p className="m-0 text-xs text-muted">Reported {timeAgo(facts.measuredAt)}.</p> : null}
    </dl>
  );
}

/** One order as a row: what, by whom, when; its state as a dot and a word; the machine's own words under it. */
function OrderRow({ o, onCancel }: { o: DeviceOrder; onCancel: () => void }) {
  return (
    <li className="grid gap-0.5 py-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="min-w-0 font-medium">{orderLine(o)}</span>
        <span className="inline-flex items-center gap-2">
          <StatusMark state={STATE_MARK[o.state]} label={STATE_TEXT[o.state]} />
          {o.state === 'pending' ? (
            <Button size="sm" variant="ghost" onPress={onCancel}>
              Cancel
            </Button>
          ) : null}
        </span>
      </div>
      <div className="text-xs text-muted">{[o.issuedBy.name ? `by ${o.issuedBy.name}` : undefined, timeAgo(o.issuedAt)].filter(Boolean).join(' · ')}</div>
      {o.message ? <div className="text-xs text-muted">{o.message}</div> : null}
    </li>
  );
}

/** The Orders part of a node's or a workbench's page. */
export function DeviceOrders({ device, onChange }: { device: { deviceId: string; hostname?: string }; onChange?: () => void }) {
  const orders = useLoad(() => routes.deviceOrders(device.deviceId).catch(() => [] as DeviceOrder[]), [device.deviceId], 10_000);
  const [kind, setKind] = useState<OrderKind>();
  const [error, setError] = useState<string>();
  const facts = factsOf(device);
  const rows = orders.data ?? [];
  return (
    <>
    <ListGroup title="Manage this machine" description="Ask it to change what it holds or how it is set up; it answers on its next report." actions={<AskMenu onChoose={setKind} />}>
      <ListBlock className="grid gap-4">
        <MachineFacts facts={facts} />
        <ErrorNote error={error} />
        {rows.length ? (
          <ul className="m-0 grid list-none divide-y divide-separator p-0 text-sm">
            {rows.slice(0, 20).map((o) => (
              <OrderRow
                key={o.id}
                o={o}
                onCancel={() =>
                  void routes
                    .cancelDeviceOrder(device.deviceId, o.id)
                    .then(() => {
                      orders.reload();
                      onChange?.();
                    })
                    .catch((e: unknown) => setError((e as Error).message))
                }
              />
            ))}
          </ul>
        ) : (
          <p className="m-0 text-sm text-muted">Nothing has been asked of this machine yet.</p>
        )}
      </ListBlock>
    </ListGroup>
      {kind && (
        <AskDialog
          kind={kind}
          facts={facts}
          deviceName={device.hostname}
          onClose={() => setKind(undefined)}
          onSend={async (k, body, force) => {
            await routes.orderDevice(device.deviceId, k, body, force);
            orders.reload();
            onChange?.();
          }}
        />
      )}
    </>
  );
}

/** A batch to every node of a project, refreshed until each has answered. */
export function OrderBatch({ projectId, batchId, initial, names, onClose }: { projectId: string; batchId: string; initial: DeviceOrderBatch; names: Record<string, string>; onClose: () => void }) {
  const [orders, setOrders] = useState(initial.orders);
  const skipped = initial.skipped ?? [];
  const [error, setError] = useState<string>();
  const waiting = orders.some((o) => o.state === 'pending');
  const refresh = () => routes.projectOrders(projectId, batchId).then((r) => setOrders(r.orders)).catch((e: unknown) => setError((e as Error).message));
  useEffect(() => {
    if (!waiting) return;
    const t = setInterval(() => void routes.projectOrders(projectId, batchId).then((r) => setOrders(r.orders)).catch((e: unknown) => setError((e as Error).message)), 4000);
    return () => clearInterval(t);
  }, [waiting, projectId, batchId]);
  return (
    <div className="mt-3 grid gap-2 rounded-lg border border-separator p-3 text-sm" role="status">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-medium">{orders[0] ? orderLine(orders[0]) : 'Your request'}</span>
        <Button size="sm" variant="ghost" onPress={onClose}>
          Dismiss
        </Button>
      </div>
      <div>{batchSummary(orders)}{skipped.length ? `; ${skipped.length} skipped` : ''}</div>
      <ErrorNote error={error} />
      <ul className="m-0 grid list-none divide-y divide-separator p-0">
        {orders.map((o) => (
          <li key={o.id} className="grid gap-0.5 py-1.5">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3">
              <span>{names[o.deviceId] ?? 'A node of this project'}</span>
              <span className="inline-flex items-center gap-2">
                <StatusMark state={STATE_MARK[o.state]} label={STATE_TEXT[o.state]} />
                {o.state === 'pending' ? (
                  <Button size="sm" variant="ghost" onPress={() => void routes.cancelProjectOrder(projectId, o.id).then(refresh).catch((e: unknown) => setError((e as Error).message))}>
                    Cancel
                  </Button>
                ) : null}
              </span>
            </div>
            {o.message ? <div className="text-xs text-muted">{o.message}</div> : null}
          </li>
        ))}
        {skipped.map((k) => (
          <li key={k.deviceId} className="grid gap-0.5 py-1.5">
            <span>{k.name}</span>
            <div className="text-xs text-muted">{k.error === 'too_big' ? `Not ordered: ${k.message}` : `Skipped: ${k.message}`}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}
